import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { ArticleResponse, ArticleType, PPSCardType, PutujmoSite, SimplePropertyModel, UpdateArticleRequest } from '../../../swagger/putujmoAdminService';
import { CrudEnum } from '../../../settings/routes';
import { Input } from '../../../components/Input/Input';
import { Button } from '../../../components/Button/Button';
import { MultiSelect } from '../../../components/MultiSelect/MultiSelect';
import { TextEditor } from '../../../components/Editor/TextEditor';
import { useGoBackFromCrudAndPush } from '../../../utils/crudHooks';
import { LanguageEnum, languageOptions, siteOptions } from '../../../utils/languageUtils';
import { getAllSimplePropertiesAction } from '../../PhobsProperties/action';
import { getSimplePropertyDisplayName } from '../../PhobsProperties/displayUtils';
import { GalleryEditor } from './GalleryEditor';

type Props = {
	values?: ArticleResponse
	onSubmit?: (newValues: UpdateArticleRequest) => Promise<void>
	onCancel?: () => void
	crudEnum: CrudEnum
}

type Mode = 'manual' | 'phobs';

const ppsCardTypeOptions = [
	{ id: PPSCardType.Accommodation, text: 'Smeštaj' },
	{ id: PPSCardType.Activity, text: 'Aktivnost' },
	{ id: PPSCardType.Restaurant, text: 'Restoran' },
];

const modeOptions = [
	{ id: 'manual' as Mode, text: 'Ručno' },
	{ id: 'phobs' as Mode, text: 'Phobs objekat' },
];

const isValidOptionalPrice = (value: string) => {
	if (!value.trim()) return true;
	const parsed = parseFloat(value.replace(',', '.'));
	return !Number.isNaN(parsed) && parsed >= 0;
};

export const PPSCardsForm = ({ values, onSubmit, onCancel, crudEnum }: Props) => {
	const isRead = crudEnum === CrudEnum.Read;

	const [putujmoSite, setPutujmoSite] = useState(values?.putujmoSite || PutujmoSite.Serbia);
	const [language, setLanguage] = useState((values?.language as LanguageEnum) || LanguageEnum.Serbian);

	const [mode, setMode] = useState<Mode>(values?.phobsPropertyId ? 'phobs' : 'manual');

	const [ppsCardType, setPpsCardType] = useState<PPSCardType | undefined>(
		values?.ppsCardType ?? (values?.phobsPropertyId ? PPSCardType.Accommodation : undefined)
	);
	const [specialConditions, setSpecialConditions] = useState(values?.specialConditions || '');

	const [title, setTitle] = useState(values?.title || '');
	const [imageUrl, setImageUrl] = useState(values?.imageUrl || '');
	const [location, setLocation] = useState(values?.location || '');
	const [content, setContent] = useState(values?.content || '');
	const [price, setPrice] = useState(values?.price || '');

	const [phobsPropertyId, setPhobsPropertyId] = useState(values?.phobsPropertyId || '');
	const [simpleProperties, setSimpleProperties] = useState<SimplePropertyModel[]>([]);

	const [gallery, setGallery] = useState<string[]>(values?.gallery || []);

	useEffect(() => {
		if (mode === 'phobs') {
			setPpsCardType(PPSCardType.Accommodation);
		}
	}, [mode]);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const res = await getAllSimplePropertiesAction(putujmoSite, language);
				if (!cancelled) setSimpleProperties(res || []);
			} catch {
				if (!cancelled) setSimpleProperties([]);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [putujmoSite, language]);

	const priceError = useMemo(() => {
		if (!price.trim()) return undefined;
		if (!isValidOptionalPrice(price)) {
			return 'Unesite validnu cenu u eurima (broj veći ili jednak 0)';
		}
		return undefined;
	}, [price]);

	const submitCallback = useCallback(
		async (e: any) => {
			e.preventDefault();

			if (mode === 'manual' && priceError) {
				toast.error(priceError);
				return;
			}

			const model = new UpdateArticleRequest();
			model.putujmoSite = putujmoSite;
			model.language = language;
			model.ppsCardType = ppsCardType;
			model.specialConditions = specialConditions;

			if (mode === 'manual') {
				model.title = title;
				model.imageUrl = imageUrl;
				model.location = location;
				model.content = content;
				model.phobsPropertyId = undefined as any;
				model.gallery = gallery;
				model.price = price.trim() || undefined;
			} else {
				model.title = '';
				model.imageUrl = '';
				model.location = '';
				model.content = '';
				model.phobsPropertyId = phobsPropertyId;
				model.gallery = [];
				model.price = undefined;
			}

			model.articleType = ArticleType.PPSCard;

			await onSubmit?.(model);
		},
		[
			putujmoSite,
			language,
			ppsCardType,
			specialConditions,
			mode,
			title,
			imageUrl,
			location,
			content,
			price,
			priceError,
			phobsPropertyId,
			gallery,
			onSubmit
		]
	);

	const goBackFromCrudAndPush = useGoBackFromCrudAndPush(CrudEnum.Read, undefined, undefined);
	const goUpdateCallback = useCallback(() => {
		if (values?.id) goBackFromCrudAndPush(`${CrudEnum.Update}/${values.id}`);
	}, [values?.id, goBackFromCrudAndPush]);

	const isSubmitDisabled = useMemo(() => {
		if (!putujmoSite || !language) return true;
		if (mode === 'manual') return !title || !!priceError;
		return !phobsPropertyId;
	}, [putujmoSite, language, mode, title, phobsPropertyId, priceError]);

	const isPhobs = mode === 'phobs';

	return (
		<form onSubmit={submitCallback}>
			<div className='flex-between-container'>
				<MultiSelect
					label='Sajt'
					value={[putujmoSite]}
					items={siteOptions}
					onChange={(value) => setPutujmoSite(value.length > 0 ? value[0] : PutujmoSite.Serbia)}
					getItemId={(item: any) => item.id}
					getItemText={(item: any) => item.text}
					disabled={isRead}
				/>
				<MultiSelect
					label='Jezik'
					value={[language]}
					items={languageOptions}
					onChange={(value) => setLanguage(value.length > 0 ? value[0] : LanguageEnum.Serbian)}
					getItemId={(item: any) => item.id}
					getItemText={(item: any) => item.text}
					disabled={isRead}
				/>
				<MultiSelect
					label='Način unosa'
					value={[mode]}
					items={modeOptions}
					onChange={(value) => setMode((value.length > 0 ? value[0] : 'manual') as Mode)}
					getItemId={(item: any) => item.id}
					getItemText={(item: any) => item.text}
					disabled={isRead}
				/>
				<MultiSelect
					label='Tip'
					value={ppsCardType !== undefined ? [ppsCardType] : []}
					items={ppsCardTypeOptions}
					onChange={(value) => setPpsCardType(value.length > 0 ? value[0] : undefined)}
					getItemId={(item: any) => item.id}
					getItemText={(item: any) => item.text}
					disabled={isRead || isPhobs}
				/>
			</div>

			{isPhobs && (
				<div className='flex-between-container'>
					<MultiSelect
						label='Phobs objekat'
						value={phobsPropertyId ? [phobsPropertyId] : []}
						items={simpleProperties}
						onChange={(value) => setPhobsPropertyId(value.length > 0 ? value[0] : '')}
						getItemId={(item: SimplePropertyModel) => item.propertyId}
						getItemText={getSimplePropertyDisplayName}
						disabled={isRead}
					/>
				</div>
			)}

			{!isPhobs && (
				<>
					<div className='flex-between-container'>
						<Input label='Naziv' value={title} onChange={(v) => setTitle(v)} disabled={isRead} />
						<Input label='Lokacija' value={location} onChange={(v) => setLocation(v)} disabled={isRead} />
						<Input
							label='Url naslovne slike'
							value={imageUrl}
							onChange={(v) => setImageUrl(v)}
							disabled={isRead}
						/>
						<Input
							label='Posebni uslovi (opciono)'
							value={specialConditions}
							onChange={(v) => setSpecialConditions(v)}
							disabled={isRead}
						/>
					</div>
					<div className='price-field'>
						<div className='label-input'>
							<label>Cena od (opciono)</label>
						</div>
						<div className='price-input-group'>
							<span className='price-input-prefix' aria-hidden='true'>
								€
							</span>
							<input
								className='input'
								type='number'
								min={0}
								step='0.01'
								value={price}
								onChange={(e) => setPrice(e.target.value)}
								disabled={isRead}
								placeholder='npr. 45'
								aria-label='Cena od u eurima'
							/>
						</div>
						<div className='price-field-hint'>Iznos u eurima (€). Prikazuje se na portalu kao cena od.</div>
						{priceError && <div className='price-field-error'>{priceError}</div>}
					</div>
					<TextEditor
						value={values?.content || ''}
						onChange={(value) => setContent(value)}
						disabled={isRead}
					/>
					<GalleryEditor value={gallery} onChange={setGallery} disabled={isRead} />
				</>
			)}

			<div className='flex-end-container'>
				{!isRead && <Button type='submit' value='Prihvati' disabled={isSubmitDisabled} />}
				{!isRead && <Button type='button' value='Odustani' onClick={onCancel} outline />}
				{isRead && <Button type='button' value='Izmeni' onClick={goUpdateCallback} />}
			</div>
		</form>
	);
};
